/**
 * Keepsy App: mountet das gemeinsame Chrome jeder Seite, genau eine Quelle wie
 * header.liquid und footer.liquid in Shopify (docs/ARCHITECTURE.md).
 *
 *   #k-announcement  Announcement Bar (auf jeder Seite, auch im Konfigurator)
 *   #k-header        Header, auf data-page="konfigurator" als Fokus-Variante
 *   #k-footer        Footer
 *   #k-overlays      Warenkorb-Drawer, Toast-Region, Cookie-Hinweis
 *
 * Zusaetzlich: Icon-Platzhalter <span data-icon="name"> werden zu Inline-SVG,
 * Klicks auf [data-action="open-cart"] oeffnen den Warenkorb, Sticker kleben
 * einmal beim Sichtbarwerden auf (Signature Move 3), und nach dem Laden raeumt ein
 * Lauf in IndexedDB Dateien weg, auf die nichts mehr zeigt.
 *
 * Im Shopify-Theme (window.KEEPSY.shopify) gilt der Cookie-Banner von Shopify, der eigene Hinweis bleibt aus.
 *
 * Exportiert nichts und legt nichts auf window ab. Seiten-Module laden danach und
 * nutzen die ui-Module direkt (z. B. openCart aus ui/cart-drawer.js).
 * @module app
 */

import { mountAnnouncement } from '@keepsy/ui-announcement.js'
import { mountHeader } from '@keepsy/ui-header.js'
import { mountFooter } from '@keepsy/ui-footer.js'
import { mountCartDrawer, openCart } from '@keepsy/ui-cart-drawer.js'
import { mountToastRegion } from '@keepsy/ui-toast.js'
import { mountCookieBanner } from '@keepsy/ui-cookie-banner.js'
import { hydrateIcons } from '@keepsy/ui-icons.js'
import { mountPaymentIcons } from '@keepsy/ui-payment-icons.js'
import { store } from '@keepsy/store.js'
import { cartBackend } from '@keepsy/cart-backend.js'
import { isShopify } from '@keepsy/routes.js'
import { db } from '@keepsy/db.js'

const page = document.body.dataset.page || ''
const isConfigurator = page === 'konfigurator'

/**
 * Fuehrt einen Mount-Schritt aus. Ein Fehler in einem Baustein darf die anderen
 * nicht verhindern (die Seite soll immer bedienbar bleiben).
 * @param {string} name
 * @param {() => void} fn
 */
function safely(name, fn) {
  try {
    fn()
  } catch (err) {
    console.error(`[keepsy/app] ${name} konnte nicht geladen werden.`, err)
  }
}

/* --------------------------------------------------------------------------
   Sticker-Pop: einmal beim Sichtbarwerden, nie in Schleife (RESEARCH 7, Regel 8).
   Was beim Laden schon sichtbar ist, steht einfach da (kein Flackern, Hero bleibt ruhig).
   -------------------------------------------------------------------------- */

/** Sticker in diesen Bereichen poppen nie (Chrome und Overlays stehen einfach da). */
const NO_POP = 'dialog, [data-no-pop], .k-header, .k-announcement, .k-cookie, .k-toast-region'

let stickerObserver = null

/**
 * Laesst einen Sticker einmal aufkleben.
 * @param {HTMLElement} el
 */
function popSticker(el) {
  el.classList.remove('is-armed')
  el.classList.add('is-popping')
  el.addEventListener('animationend', () => el.classList.remove('is-popping'), { once: true })
}

/**
 * Liegt das Element (zumindest teilweise) im sichtbaren Bereich?
 * @param {Element} el
 * @returns {boolean}
 */
function inViewport(el) {
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return true
  return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth
}

/**
 * Bereitet Sticker vor: unsichtbar bis zum Sichtbarwerden, dann Pop.
 * @param {Element[]} stickers
 * @param {{ popVisibleNow?: boolean }} [options] popVisibleNow: gerade eingefuegte,
 *   noch nie gezeichnete Sticker im Viewport sofort poppen lassen
 */
function armStickers(stickers, options = {}) {
  if (!stickerObserver) return
  for (const el of stickers) {
    if (el.dataset.popped || el.closest(NO_POP)) continue
    el.dataset.popped = 'true'
    if (inViewport(el)) {
      if (options.popVisibleNow) popSticker(el)
      continue
    }
    el.classList.add('is-armed')
    stickerObserver.observe(el)
  }
}

/** Startet den Sticker-Pop fuer vorhandene und spaeter eingefuegte Sticker. */
function initStickerPop() {
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return
  stickerObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      stickerObserver.unobserve(entry.target)
      popSticker(entry.target)
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.5 })

  armStickers(Array.from(document.querySelectorAll('.k-sticker')))

  new MutationObserver((records) => {
    const added = []
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue
        if (node.matches('.k-sticker')) added.push(node)
        added.push(...node.querySelectorAll('.k-sticker'))
      }
    }
    if (added.length) armStickers(added, { popVisibleNow: true })
  }).observe(document.getElementById('main') || document.body, { childList: true, subtree: true })
}

/* --------------------------------------------------------------------------
   Aufraeumen in IndexedDB: Druckdateien entfernter Artikel, Fotos, die aus dem
   Upload-Verlauf gefallen sind, und Dateien verworfener Bearbeitungen.
   Referenziert ist jede Datei, deren Key irgendwo in Warenkorb, Entwurf oder letzter
   Bestellung steht. Geloescht wird erst, wenn ein Key in diesem Tab seit mindestens
   einer Minute unreferenziert ist: ein Foto, das ein anderer Tab gerade hochlaedt, oder
   eine Druckdatei kurz vor store.addItem bleibt so sicher erhalten.
   -------------------------------------------------------------------------- */

/** sessionStorage-Key fuer "seit wann unreferenziert" (UI-Hilfswert, kein Warenkorb-Zustand). */
const SWEEP_KEY = 'keepsy.ui.file-sweep'

/** Mindestzeit ohne Referenz, bevor eine Datei geloescht wird. */
const SWEEP_GRACE_MS = 60 * 1000

/**
 * Sammelt alle Strings aus einem gespeicherten Objekt (Keys stehen in srcKey, printFileKey,
 * uploads[].srcKey, collage.slots[].srcKey; so faellt keine kuenftige Stelle durch).
 * @param {unknown} value
 * @param {Set<string>} out
 */
function collectStrings(value, out) {
  if (typeof value === 'string') {
    // Thumbnails (DataURLs) sind nie Keys
    if (value.length < 200) out.add(value)
    return
  }
  if (!value || typeof value !== 'object') return
  for (const child of Array.isArray(value) ? value : Object.values(value)) collectStrings(child, out)
}

function readSweep() {
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(SWEEP_KEY) || '{}')
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  } catch {
    return {}
  }
}

function writeSweep(map) {
  try {
    window.sessionStorage.setItem(SWEEP_KEY, JSON.stringify(map))
  } catch {
    // Speicher blockiert: dann wird beim naechsten Mal nur markiert, nie vorschnell geloescht
  }
}

/** Ein Aufraeumlauf. Fehler bleiben still, die Seite funktioniert auch ohne. */
async function sweepFiles() {
  const keys = await db.listKeys()
  if (!keys.length) {
    writeSweep({})
    return
  }
  const referenced = new Set()
  collectStrings(cartBackend.getCart(), referenced)
  collectStrings(store.getDraft(), referenced)
  collectStrings(store.getOrder(), referenced)
  const seen = readSweep()
  const next = {}
  const now = Date.now()
  for (const key of keys) {
    if (referenced.has(key)) continue
    const since = Number(seen[key])
    if (Number.isFinite(since) && now - since >= SWEEP_GRACE_MS) {
      await db.deleteFile(key).catch(() => null)
    } else {
      next[key] = Number.isFinite(since) ? since : now
    }
  }
  writeSweep(next)
}

function scheduleSweep() {
  const run = () => sweepFiles().catch(() => null)
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 5000 })
  else window.setTimeout(run, 2000)
}

/* --------------------------------------------------------------------------
   Mounten
   -------------------------------------------------------------------------- */

const overlays = document.getElementById('k-overlays') || document.body

// Seit 04.10.2026 auch im Konfigurator (Louis): ganz oben, scrollt weg, der Fokus-Header klebt darunter
safely('Announcement Bar', () => mountAnnouncement(document.getElementById('k-announcement')))

safely('Header', () => mountHeader(document.getElementById('k-header'), {
  variant: isConfigurator ? 'focus' : 'default',
  page,
  overlays,
}))
safely('Footer', () => mountFooter(document.getElementById('k-footer'), { page }))
safely('Warenkorb', () => mountCartDrawer(overlays))
safely('Meldungen', () => mountToastRegion(overlays))
// Im Shopify-Theme uebernimmt der Cookie-Banner von Shopify (Einstellungen > Kundendatenschutz)
// Im Shop nur mit Theme-Einstellung "Eigener Cookie-Banner" (ui/cookie-banner.js), sonst gilt der Banner von Shopify
safely('Cookie-Banner', () => { mountCookieBanner() })
safely('Bezahl-Logos', () => mountPaymentIcons(document))
safely('Icons', () => hydrateIcons(document))

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-action="open-cart"]')
  if (!trigger) return
  event.preventDefault()
  openCart()
})

/**
 * Deko-Huellen mit data-mockup (FAQ, 404, Rechtsseiten) als echtes Handy in klarer Huelle zeichnen.
 * Der Renderer laedt nur, wenn die Seite solche Huellen hat (ui/case-mockups.js).
 */
function mountDecoCases() {
  if (!document.querySelector('.k-case[data-mockup]')) return
  import('@keepsy/ui-case-mockups.js')
    .then((mod) => mod.mountCaseMockups(document))
    .catch((err) => console.warn('[keepsy/app] Deko-Huellen bleiben als CSS-Huelle.', err))
}

// Seiten-Module laufen nach app.js. Sticker erst danach erfassen, damit auch ihr Markup zaehlt.
const startStickers = () => {
  safely('Sticker', initStickerPop)
  safely('Deko-Huellen', mountDecoCases)
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startStickers, { once: true })
} else {
  startStickers()
}

// Aufraeumen erst, wenn die Seite steht und der Browser Luft hat
if (document.readyState === 'complete') scheduleSweep()
else window.addEventListener('load', scheduleSweep, { once: true })
